export type TimedModeId = "challenge-1" | "standard" | "challenge-5";
export type ModeId = TimedModeId | "free-play";
export type TimedRoundDurationMs = 60_000 | 180_000 | 300_000;
export type TimedRoundWarningMs = 15_000 | 45_000 | 60_000;
export type UpgradeId = "starting-size" | "movement-speed" | "food-value";
export type SkinId =
  | "garden-sprout"
  | "berry-bright"
  | "sunbeam-stripes"
  | "pond-ripple"
  | "cloud-confetti"
  | "lavender-loop"
  | "firefly-freckles"
  | "moonlit-mint"
  | "coral-comet"
  | "rainbow-ribbon";
export type FoodId = "berry" | "citrus" | "melon" | "starfruit";
export type BonusId = "turbo";
export type BotTargetPreference = "food" | "hunt" | "wander";
export type RewardId = "bot-defeat";
export type ArenaId = "sunlit-garden";

export type ModeConfig =
  | {
      readonly description: string;
      readonly displayName: string;
      readonly finalWarningMs: TimedRoundWarningMs;
      readonly id: TimedModeId;
      readonly roundDurationMs: TimedRoundDurationMs;
    }
  | {
      readonly description: string;
      readonly displayName: string;
      readonly finalWarningMs: null;
      readonly id: "free-play";
      readonly roundDurationMs: null;
    };

export interface UpgradeConfig {
  readonly costs: readonly number[];
  readonly effects: readonly number[];
  readonly id: UpgradeId;
  readonly levels: readonly number[];
  readonly maxLevel: number;
}

export interface SkinConfig {
  readonly accentColor: number;
  readonly bodyColor: number;
  readonly displayName: string;
  readonly id: SkinId;
  readonly initiallyUnlocked: boolean;
  readonly pattern:
    | "confetti"
    | "freckles"
    | "leaf"
    | "loops"
    | "ripples"
    | "spots"
    | "stripes"
    | "crescent";
  readonly unlockCost: number | null;
}

export interface FoodConfig {
  readonly growth: number;
  readonly id: FoodId;
  readonly radius: number;
}

export interface FoodPopulationConfig {
  readonly debugNearMaximumDistance: number;
  readonly debugNearMinimumDistance: number;
  readonly initialPlayerExclusionRadius: number;
  readonly maximum: number;
  readonly maximumSpawnAttemptsPerFood: number;
  readonly minimum: number;
  readonly minimumFoodSpacing: number;
  readonly spawnInset: number;
  readonly target: number;
}

export interface BonusConfig {
  readonly durationMs: number;
  readonly id: BonusId;
  readonly radius: number;
}

export interface BonusPopulationConfig {
  readonly initialPlayerExclusionRadius: number;
  readonly maximumSpawnAttemptsPerBonus: number;
  readonly minimumSpacing: number;
  readonly spawnInset: number;
  readonly target: number;
}

export interface BotRosterConfig {
  readonly aggressionMaximum: number;
  readonly aggressionMinimum: number;
  readonly debugNearMaximumDistance: number;
  readonly debugNearMinimumDistance: number;
  readonly fleeLevelGap: number;
  readonly huntLevelGap: number;
  readonly initialPlayerExclusionRadius: number;
  readonly maximumLevel: number;
  readonly maximumLevelAbovePlayer: number;
  readonly maximumActive: number;
  readonly maximumSpawnAttemptsPerBot: number;
  readonly minimumLevel: number;
  readonly minimumActive: number;
  readonly minimumSpacing: number;
  readonly movementSpeed: number;
  readonly perceptionRadius: number;
  readonly perceptionRadiusMaximum: number;
  readonly perceptionRadiusMinimum: number;
  readonly regenerationCadenceMs: number;
  readonly regenerationFallbackColumns: number;
  readonly regenerationFallbackRows: number;
  readonly regenerationPlayerClearance: number;
  readonly recoveryDurationMs: number;
  readonly retargetCooldownMs: number;
  readonly spawnInset: number;
  readonly steeringNoiseMaximum: number;
  readonly steeringNoiseMinimum: number;
  readonly stuckDetectionMs: number;
  readonly stuckDistanceEpsilon: number;
  readonly targetLevelHysteresis: number;
  readonly targetRangeHysteresis: number;
  readonly targetSwitchMinimumAdvantage: number;
  readonly targetActive: number;
  readonly targetPreferences: readonly BotTargetPreference[];
  readonly turnRateRadiansPerSecond: number;
  readonly wanderTargetReachDistance: number;
}

export interface RewardConfig {
  readonly coins: number;
  readonly id: RewardId;
}

export interface SimulationClockConfig {
  readonly fixedStepMs: number;
  readonly maximumRealFrameDeltaMs: number;
}

export interface PlayerBodyConfig {
  readonly baseSegmentCount: number;
  readonly bodyRadiusScale: number;
  readonly historyMaximumDistance: number;
  readonly interpolationDistance: number;
  readonly levelBandSize: number;
  readonly minimumTailRadius: number;
  readonly rippleAmplitude: number;
  readonly rippleDurationMs: number;
  readonly rippleWaveLength: number;
  readonly segmentSpacingScale: number;
  readonly tailRadiusScale: number;
}

export interface SnakeVisualConfig {
  readonly anatomyId: string;
  readonly bodyOverlapScale: number;
  readonly bodyWidthScale: number;
  readonly eyeForwardScale: number;
  readonly eyeRadiusScale: number;
  readonly eyeSideScale: number;
  readonly eyeHighlightForwardScale: number;
  readonly eyeHighlightRadiusScale: number;
  readonly headLengthScale: number;
  readonly headWidthScale: number;
  readonly homeLevel: number;
  readonly mouthForwardScale: number;
  readonly maximumUnscaledParts: number;
  readonly neckRadiusScale: number;
  readonly outlineColor: number;
  readonly outlineWidthScale: number;
  readonly patternStepPx: number;
  readonly tailBaseScale: number;
  readonly tailPointScale: number;
  readonly tailTipRadiusScale: number;
  readonly tongueForkScale: number;
  readonly tongueLengthScale: number;
}

export interface SnakeMotionConfig {
  readonly blinkCycleMs: number;
  readonly blinkDurationMs: number;
  readonly chompDurationMs: number;
  readonly mouthOpenScale: number;
  readonly slitherAmplitudeScale: number;
  readonly slitherCycleMs: number;
  readonly slitherWavelengthSegments: number;
  readonly tongueFlickScale: number;
}

export const getSnakeBodySegmentCount = (
  level: number,
  config: PlayerBodyConfig,
): number => {
  if (!Number.isSafeInteger(level) || level < 1) {
    throw new RangeError("Player body level must be a positive integer");
  }
  const finalBand = Math.min(
    Math.ceil(level / config.levelBandSize),
    config.levelBandSize,
  );
  let additions = Math.min(level - 1, config.levelBandSize - 1);
  for (let band = 2; band <= finalBand; band += 1) {
    const firstLevel = (band - 1) * config.levelBandSize + 1;
    const finalLevel = Math.min(level, band * config.levelBandSize);
    additions += Math.floor((finalLevel - firstLevel + 1) / band);
  }
  return config.baseSegmentCount + additions;
};

export interface PlayerStatusConfig {
  readonly badgeGap: number;
  readonly feedbackDurationMs: number;
  readonly feedbackGap: number;
  readonly markerGap: number;
  readonly markerRadius: number;
}

export interface CoinRewardFeedbackConfig {
  readonly sparkCount: number;
  readonly sparkRadius: number;
  readonly sparkStartDistance: number;
  readonly sparkTravelDistance: number;
}

export interface StrengthReadabilityConfig {
  readonly opponentCueDistance: number;
}

export interface TailClippingConfig {
  readonly bodySeparationDistance: number;
  readonly feedbackDurationMs: number;
  readonly mouthForwardScale: number;
  readonly mouthRadiusScale: number;
}

export interface VirtualJoystickConfig {
  readonly deadZoneRadius: number;
  readonly knobRadius: number;
  readonly ringRadius: number;
  readonly safeAreaInset: number;
  readonly visual: {
    readonly connectorAlpha: number;
    readonly fillAlpha: number;
    readonly knobAlpha: number;
    readonly outlineAlpha: number;
    readonly ringAlpha: number;
  };
}

export interface ArenaConfig {
  readonly boundaryInset: number;
  readonly boundaryPushDistance: number;
  readonly cameraFollowRatePerSecond: number;
  readonly cameraLookAheadDistance: number;
  readonly height: number;
  readonly id: ArenaId;
  readonly width: number;
}

export interface GameplayConfig {
  readonly arena: ArenaConfig;
  readonly bonusPopulation: BonusPopulationConfig;
  readonly bonuses: readonly BonusConfig[];
  readonly bots: BotRosterConfig;
  readonly combat: {
    readonly contactDistancePadding: number;
    readonly contactMaximumAgeMs: number;
    readonly defeatLevelGap: number;
    readonly playerRespawnDelayMs: number;
    readonly playerRespawnGridColumns: number;
    readonly playerRespawnGridRows: number;
    readonly playerRespawnLevelLoss: number;
    readonly playerRespawnSafeDistance: number;
    readonly playerRespawnSpawnInset: number;
    readonly separationDistance: number;
  };
  readonly coinRewardFeedback: CoinRewardFeedbackConfig;
  readonly defaultSkinId: SkinId;
  readonly foodPopulation: FoodPopulationConfig;
  readonly foods: readonly FoodConfig[];
  readonly growth: {
    readonly growthRequirementsThroughLevelTen: readonly number[];
    readonly maximumGrowthRequirement: number;
    readonly maximumLevel: number;
    readonly maximumRadius: number;
    readonly radiusIncreasePerLevel: number;
    readonly sizes: readonly number[];
    readonly startingRadius: number;
    readonly startingLevel: number;
    readonly thresholds: readonly number[];
  };
  readonly modes: readonly ModeConfig[];
  readonly movement: {
    readonly acceleration: number;
    readonly baseSpeed: number;
    readonly initialHeadingRadians: number;
    readonly initialPosition: {
      readonly x: number;
      readonly y: number;
    };
    readonly maximumSpeed: number;
    readonly turnRateRadiansPerSecond: number;
  };
  readonly playerBody: PlayerBodyConfig;
  readonly playerStatus: PlayerStatusConfig;
  readonly rewards: {
    readonly botDefeatPlayerRewardOnly: boolean;
    readonly botFoodDropMaximum: number;
    readonly botFoodDropMaximumDistance: number;
    readonly botFoodDropMinimum: number;
    readonly botFoodDropMinimumDistance: number;
    readonly entries: readonly RewardConfig[];
  };
  readonly round: {
    readonly durationMs: number;
  };
  readonly runtimeStability: {
    readonly maximumProcessedEvents: number;
  };
  readonly simulation: SimulationClockConfig;
  readonly skins: readonly SkinConfig[];
  readonly snakeMotion: SnakeMotionConfig;
  readonly snakeVisual: SnakeVisualConfig;
  readonly strengthReadability: StrengthReadabilityConfig;
  readonly tailClipping: TailClippingConfig;
  readonly upgrades: readonly UpgradeConfig[];
  readonly virtualJoystick: VirtualJoystickConfig;
}

const expectedModeIds = [
  "challenge-1",
  "standard",
  "challenge-5",
  "free-play",
] as const;
const expectedBonusIds = ["turbo"] as const;
const expectedUpgradeIds = [
  "starting-size",
  "movement-speed",
  "food-value",
] as const;

export const maximumSnakeLevel = 100_000;

interface SnakeGrowthTuning {
  readonly growthRequirementsThroughLevelTen: readonly number[];
  readonly maximumGrowthRequirement: number;
  readonly maximumLevel: number;
  readonly maximumRadius: number;
  readonly radiusIncreasePerLevel: number;
  readonly startingRadius: number;
}

const snakeGrowthTuning = {
  growthRequirementsThroughLevelTen: [8, 12, 18, 24, 30, 38, 46, 54, 62],
  maximumGrowthRequirement: 62,
  maximumLevel: maximumSnakeLevel,
  maximumRadius: 48,
  radiusIncreasePerLevel: 2,
  startingRadius: 18,
} as const satisfies SnakeGrowthTuning;

const createGrowthThresholds = (
  tuning: SnakeGrowthTuning,
): readonly number[] => {
  let threshold = 0;
  return Array.from({ length: tuning.maximumLevel }, (_value, index) => {
    if (index === 0) {
      return threshold;
    }
    threshold +=
      tuning.growthRequirementsThroughLevelTen[index - 1] ??
      tuning.maximumGrowthRequirement;
    return threshold;
  });
};

const createGrowthSizes = (tuning: SnakeGrowthTuning): readonly number[] =>
  Array.from({ length: tuning.maximumLevel }, (_value, index) =>
    Math.min(
      tuning.startingRadius + index * tuning.radiusIncreasePerLevel,
      tuning.maximumRadius,
    ),
  );

export const gameplayConfig = {
  arena: {
    boundaryInset: 72,
    boundaryPushDistance: 12,
    cameraFollowRatePerSecond: 5,
    cameraLookAheadDistance: 180,
    height: 2_400,
    id: "sunlit-garden",
    width: 3_600,
  },
  bonusPopulation: {
    initialPlayerExclusionRadius: 260,
    maximumSpawnAttemptsPerBonus: 100,
    minimumSpacing: 96,
    spawnInset: 40,
    target: 3,
  },
  bonuses: [{ durationMs: 2_500, id: "turbo", radius: 19 }],
  bots: {
    aggressionMaximum: 0.9,
    aggressionMinimum: 0.2,
    debugNearMaximumDistance: 180,
    debugNearMinimumDistance: 100,
    fleeLevelGap: 2,
    huntLevelGap: 2,
    initialPlayerExclusionRadius: 220,
    maximumLevel: maximumSnakeLevel,
    maximumLevelAbovePlayer: 5,
    maximumActive: 20,
    maximumSpawnAttemptsPerBot: 80,
    minimumLevel: 1,
    minimumActive: 12,
    minimumSpacing: 18,
    movementSpeed: 118,
    perceptionRadius: 360,
    perceptionRadiusMaximum: 440,
    perceptionRadiusMinimum: 240,
    regenerationCadenceMs: 5_000,
    regenerationFallbackColumns: 12,
    regenerationFallbackRows: 8,
    regenerationPlayerClearance: 120,
    recoveryDurationMs: 500,
    retargetCooldownMs: 600,
    spawnInset: 24,
    steeringNoiseMaximum: 0.3,
    steeringNoiseMinimum: 0.05,
    stuckDetectionMs: 800,
    stuckDistanceEpsilon: 2,
    targetLevelHysteresis: 1,
    targetRangeHysteresis: 60,
    targetSwitchMinimumAdvantage: 36,
    targetActive: 16,
    targetPreferences: ["wander", "food", "hunt"],
    turnRateRadiansPerSecond: 2.4,
    wanderTargetReachDistance: 28,
  },
  combat: {
    contactDistancePadding: 0,
    contactMaximumAgeMs: 40,
    defeatLevelGap: 2,
    playerRespawnDelayMs: 1_200,
    playerRespawnGridColumns: 6,
    playerRespawnGridRows: 4,
    playerRespawnLevelLoss: 1,
    playerRespawnSafeDistance: 180,
    playerRespawnSpawnInset: 40,
    separationDistance: 24,
  },
  coinRewardFeedback: {
    sparkCount: 8,
    sparkRadius: 3,
    sparkStartDistance: 24,
    sparkTravelDistance: 28,
  },
  defaultSkinId: "garden-sprout",
  foodPopulation: {
    debugNearMaximumDistance: 300,
    debugNearMinimumDistance: 72,
    initialPlayerExclusionRadius: 180,
    maximum: 180,
    maximumSpawnAttemptsPerFood: 80,
    minimum: 90,
    minimumFoodSpacing: 8,
    spawnInset: 12,
    target: 135,
  },
  foods: [
    { growth: 2, id: "berry", radius: 12 },
    { growth: 3, id: "citrus", radius: 14 },
    { growth: 4, id: "melon", radius: 13 },
    { growth: 5, id: "starfruit", radius: 15 },
  ],
  growth: {
    ...snakeGrowthTuning,
    sizes: createGrowthSizes(snakeGrowthTuning),
    startingLevel: 1,
    thresholds: createGrowthThresholds(snakeGrowthTuning),
  },
  modes: [
    {
      description: "A quick full run",
      displayName: "1 minute",
      finalWarningMs: 15_000,
      id: "challenge-1",
      roundDurationMs: 60_000,
    },
    {
      description: "The classic challenge",
      displayName: "3 minutes",
      finalWarningMs: 45_000,
      id: "standard",
      roundDurationMs: 180_000,
    },
    {
      description: "Go for your biggest snake",
      displayName: "5 minutes",
      finalWarningMs: 60_000,
      id: "challenge-5",
      roundDurationMs: 300_000,
    },
    {
      description: "No clock. Explore your way",
      displayName: "Free Play",
      finalWarningMs: null,
      id: "free-play",
      roundDurationMs: null,
    },
  ],
  movement: {
    acceleration: 400,
    baseSpeed: 160,
    initialHeadingRadians: 0,
    initialPosition: {
      x: 1_800,
      y: 1_200,
    },
    maximumSpeed: 240,
    turnRateRadiansPerSecond: 3.2,
  },
  playerBody: {
    baseSegmentCount: 4,
    bodyRadiusScale: 0.82,
    historyMaximumDistance: 1_300,
    interpolationDistance: 4,
    levelBandSize: 10,
    minimumTailRadius: 8,
    rippleAmplitude: 0.12,
    rippleDurationMs: 360,
    rippleWaveLength: 0.42,
    segmentSpacingScale: 0.85,
    tailRadiusScale: 0.45,
  },
  snakeMotion: {
    blinkCycleMs: 3_200,
    blinkDurationMs: 160,
    chompDurationMs: 280,
    mouthOpenScale: 0.3,
    slitherAmplitudeScale: 0.16,
    slitherCycleMs: 900,
    slitherWavelengthSegments: 5,
    tongueFlickScale: 0.55,
  },
  snakeVisual: {
    anatomyId: "snake-v4-clear-face",
    bodyOverlapScale: 1.12,
    bodyWidthScale: 0.62,
    eyeForwardScale: 0.22,
    eyeHighlightForwardScale: 0.035,
    eyeHighlightRadiusScale: 0.035,
    eyeRadiusScale: 0.13,
    eyeSideScale: 0.4,
    headLengthScale: 1.32,
    headWidthScale: 0.68,
    homeLevel: 3,
    maximumUnscaledParts: 12,
    mouthForwardScale: 0.72,
    neckRadiusScale: 0.42,
    outlineColor: 0x102a43,
    outlineWidthScale: 0.1,
    patternStepPx: 36,
    tailBaseScale: 0.42,
    tailPointScale: 0.76,
    tailTipRadiusScale: 0.11,
    tongueForkScale: 0.12,
    tongueLengthScale: 0.32,
  },
  playerStatus: {
    badgeGap: 18,
    feedbackDurationMs: 900,
    feedbackGap: 18,
    markerGap: 8,
    markerRadius: 8,
  },
  rewards: {
    botDefeatPlayerRewardOnly: true,
    botFoodDropMaximum: 5,
    botFoodDropMaximumDistance: 90,
    botFoodDropMinimum: 3,
    botFoodDropMinimumDistance: 24,
    entries: [{ coins: 4, id: "bot-defeat" }],
  },
  round: {
    durationMs: 180_000,
  },
  runtimeStability: {
    maximumProcessedEvents: 256,
  },
  simulation: {
    fixedStepMs: 20,
    maximumRealFrameDeltaMs: 50,
  },
  skins: [
    {
      accentColor: 0xffe066,
      bodyColor: 0x68d391,
      displayName: "Emerald Fang",
      id: "garden-sprout",
      initiallyUnlocked: true,
      pattern: "leaf",
      unlockCost: null,
    },
    {
      accentColor: 0xffd6e0,
      bodyColor: 0xd94f70,
      displayName: "Berry Viper",
      id: "berry-bright",
      initiallyUnlocked: false,
      pattern: "spots",
      unlockCost: 15,
    },
    {
      accentColor: 0xfff3b0,
      bodyColor: 0xf59e0b,
      displayName: "Solar Stripe",
      id: "sunbeam-stripes",
      initiallyUnlocked: false,
      pattern: "stripes",
      unlockCost: 25,
    },
    {
      accentColor: 0xbde0fe,
      bodyColor: 0x2889a8,
      displayName: "Blue Racer",
      id: "pond-ripple",
      initiallyUnlocked: false,
      pattern: "ripples",
      unlockCost: 40,
    },
    {
      accentColor: 0xffd166,
      bodyColor: 0x8ecae6,
      displayName: "Stormscale",
      id: "cloud-confetti",
      initiallyUnlocked: false,
      pattern: "confetti",
      unlockCost: 60,
    },
    {
      accentColor: 0xf7d6ff,
      bodyColor: 0x9b72cf,
      displayName: "Purple Coil",
      id: "lavender-loop",
      initiallyUnlocked: false,
      pattern: "loops",
      unlockCost: 90,
    },
    {
      accentColor: 0xfff1a8,
      bodyColor: 0x397367,
      displayName: "Night Adder",
      id: "firefly-freckles",
      initiallyUnlocked: false,
      pattern: "freckles",
      unlockCost: 130,
    },
    {
      accentColor: 0xd8fff2,
      bodyColor: 0x355070,
      displayName: "Moon Cobra",
      id: "moonlit-mint",
      initiallyUnlocked: false,
      pattern: "crescent",
      unlockCost: 180,
    },
  ],
  strengthReadability: {
    opponentCueDistance: 420,
  },
  tailClipping: {
    bodySeparationDistance: 8,
    feedbackDurationMs: 700,
    mouthForwardScale: 0.72,
    mouthRadiusScale: 0.58,
  },
  upgrades: [
    {
      costs: [25, 60, 120, 220],
      effects: [1, 2, 3, 4, 5],
      id: "starting-size",
      levels: [0, 1, 2, 3, 4],
      maxLevel: 4,
    },
    {
      costs: [30, 70, 135, 240],
      effects: [160, 172, 184, 196, 208],
      id: "movement-speed",
      levels: [0, 1, 2, 3, 4],
      maxLevel: 4,
    },
    {
      costs: [20, 55, 110, 200],
      effects: [1, 1.15, 1.3, 1.45, 1.6],
      id: "food-value",
      levels: [0, 1, 2, 3, 4],
      maxLevel: 4,
    },
  ],
  virtualJoystick: {
    deadZoneRadius: 12,
    knobRadius: 24,
    ringRadius: 72,
    safeAreaInset: 20,
    visual: {
      connectorAlpha: 0.38,
      fillAlpha: 0.18,
      knobAlpha: 0.62,
      outlineAlpha: 0.58,
      ringAlpha: 0.55,
    },
  },
} as const satisfies GameplayConfig;

export const isTimedModeId = (mode: string): mode is TimedModeId =>
  mode === "challenge-1" || mode === "standard" || mode === "challenge-5";

export const isModeId = (mode: string): mode is ModeId =>
  isTimedModeId(mode) || mode === "free-play";

export const getModeConfig = (modeId: ModeId): ModeConfig => {
  const modes: readonly ModeConfig[] = gameplayConfig.modes;
  const mode = modes.find((candidate) => candidate.id === modeId);
  if (mode === undefined) {
    throw new Error(`Missing configured game mode: ${modeId}`);
  }
  return mode;
};

const assertCondition: (
  condition: boolean,
  message: string,
) => asserts condition = (condition, message) => {
  if (!condition) {
    throw new Error(message);
  }
};

const validateFiniteNumber = (value: number, name: string): void => {
  assertCondition(Number.isFinite(value), `${name} must be finite`);
};

const validateAlpha = (value: number, name: string): void => {
  validateFiniteNumber(value, name);
  assertCondition(value >= 0 && value <= 1, `${name} must be from zero to one`);
};

const validatePositiveNumber = (value: number, name: string): void => {
  validateFiniteNumber(value, name);
  assertCondition(value > 0, `${name} must be positive`);
};

const validatePositiveInteger = (value: number, name: string): void => {
  validatePositiveNumber(value, name);
  assertCondition(Number.isInteger(value), `${name} must be an integer`);
};

const validateNonNegativeInteger = (value: number, name: string): void => {
  validateFiniteNumber(value, name);
  assertCondition(value >= 0, `${name} must not be negative`);
  assertCondition(Number.isInteger(value), `${name} must be an integer`);
};

const validateIdentifier = (value: string, name: string): void => {
  assertCondition(value.length > 0, `${name} must not be empty`);
  assertCondition(
    value.trim() === value,
    `${name} must not contain whitespace`,
  );
};

const validateUniqueIds = (
  entries: readonly { readonly id: string }[],
  name: string,
): Set<string> => {
  const ids = new Set<string>();
  for (const entry of entries) {
    validateIdentifier(entry.id, `${name} identifier`);
    assertCondition(!ids.has(entry.id), `${name} identifiers must be unique`);
    ids.add(entry.id);
  }
  return ids;
};

const validateExactIds = (
  entries: readonly { readonly id: string }[],
  expectedIds: readonly string[],
  name: string,
): void => {
  assertCondition(
    entries.length === expectedIds.length,
    `${name} must contain the exact identifier set`,
  );
  const ids = validateUniqueIds(entries, name);
  for (const expectedId of expectedIds) {
    assertCondition(
      ids.has(expectedId),
      `${name} must contain the exact identifier set`,
    );
  }
};

const validateStrictlyIncreasing = (
  values: readonly number[],
  name: string,
): void => {
  let previous: number | undefined;
  for (const value of values) {
    validateFiniteNumber(value, name);
    if (previous !== undefined) {
      assertCondition(value > previous, `${name} must be strictly increasing`);
    }
    previous = value;
  }
};

const maximumConfiguredValue = (values: readonly number[]): number =>
  values.reduce(
    (maximum, value) => Math.max(maximum, value),
    Number.NEGATIVE_INFINITY,
  );

const readModeDuration = (mode: ModeConfig): number | null =>
  mode.roundDurationMs;

const validateModes = (config: GameplayConfig): void => {
  const oneMinute = config.modes.find((mode) => mode.id === "challenge-1");
  const standard = config.modes.find((mode) => mode.id === "standard");
  const fiveMinutes = config.modes.find((mode) => mode.id === "challenge-5");
  const freePlay = config.modes.find((mode) => mode.id === "free-play");
  assertCondition(oneMinute !== undefined, "one-minute mode must exist");
  assertCondition(standard !== undefined, "standard mode must exist");
  assertCondition(fiveMinutes !== undefined, "five-minute mode must exist");
  assertCondition(freePlay !== undefined, "free-play mode must exist");
  validateExactIds(config.modes, expectedModeIds, "modes");
  assertCondition(
    oneMinute.roundDurationMs === 60_000,
    "one-minute mode duration must be exactly 60000 milliseconds",
  );
  assertCondition(
    standard.roundDurationMs === config.round.durationMs,
    "standard mode duration must match the round duration",
  );
  assertCondition(
    fiveMinutes.roundDurationMs === 300_000,
    "five-minute mode duration must be exactly 300000 milliseconds",
  );
  assertCondition(
    readModeDuration(freePlay) === null,
    "free-play must not have a round duration",
  );
  for (const mode of config.modes) {
    assertCondition(mode.displayName.length > 0, "mode name must not be empty");
    assertCondition(
      mode.description.length > 0,
      "mode description must not be empty",
    );
    if (mode.roundDurationMs !== null) {
      validatePositiveInteger(mode.finalWarningMs, "mode final warning");
      assertCondition(
        mode.finalWarningMs < mode.roundDurationMs,
        "mode final warning must precede its duration",
      );
    }
  }
};

const validateGrowth = (config: GameplayConfig): void => {
  validatePositiveInteger(config.growth.maximumLevel, "maximum snake level");
  assertCondition(
    config.growth.maximumLevel === maximumSnakeLevel,
    `maximum snake level must be ${String(maximumSnakeLevel)}`,
  );
  assertCondition(
    config.growth.growthRequirementsThroughLevelTen.length === 9,
    "early growth requirements must cover Levels 1 through 10",
  );
  validateStrictlyIncreasing(
    config.growth.growthRequirementsThroughLevelTen,
    "early growth requirements",
  );
  validatePositiveNumber(
    config.growth.maximumGrowthRequirement,
    "maximum growth requirement",
  );
  assertCondition(
    config.growth.growthRequirementsThroughLevelTen.every(
      (requirement) => requirement <= config.growth.maximumGrowthRequirement,
    ),
    "maximum growth requirement must include every early requirement",
  );
  validatePositiveNumber(config.growth.startingRadius, "starting snake radius");
  validatePositiveNumber(
    config.growth.radiusIncreasePerLevel,
    "snake radius increase",
  );
  validatePositiveNumber(config.growth.maximumRadius, "maximum snake radius");
  assertCondition(
    config.growth.maximumRadius >= config.growth.startingRadius,
    "maximum snake radius must include the starting radius",
  );
  assertCondition(
    config.growth.thresholds.length === config.growth.sizes.length,
    "growth thresholds and sizes must align",
  );
  assertCondition(
    config.growth.thresholds.length === config.growth.maximumLevel,
    "growth tables must contain every snake level",
  );
  assertCondition(
    config.growth.thresholds[0] === 0,
    "growth must start at zero",
  );
  validateStrictlyIncreasing(config.growth.thresholds, "growth thresholds");
  for (const size of config.growth.sizes) {
    validatePositiveNumber(size, "growth size");
  }
  const expectedThresholds = createGrowthThresholds(config.growth);
  const expectedSizes = createGrowthSizes(config.growth);
  assertCondition(
    config.growth.thresholds.every(
      (threshold, index) => threshold === expectedThresholds[index],
    ),
    "growth thresholds must follow the configured rule",
  );
  assertCondition(
    config.growth.sizes.every((size, index) => size === expectedSizes[index]),
    "growth sizes must follow the configured radius rule",
  );
  validatePositiveInteger(config.growth.startingLevel, "starting level");
  assertCondition(
    config.growth.startingLevel <= config.growth.sizes.length,
    "starting level must exist in the growth table",
  );
};

const validateUpgrades = (config: GameplayConfig): void => {
  validateExactIds(config.upgrades, expectedUpgradeIds, "upgrades");
  for (const upgrade of config.upgrades) {
    validatePositiveInteger(upgrade.maxLevel, `${upgrade.id} maximum level`);
    assertCondition(
      upgrade.levels.length === upgrade.maxLevel + 1,
      `${upgrade.id} levels must align with its cap`,
    );
    assertCondition(
      upgrade.effects.length === upgrade.levels.length,
      `${upgrade.id} effects must align with its levels`,
    );
    assertCondition(
      upgrade.costs.length === upgrade.maxLevel,
      `${upgrade.id} costs must align with its cap`,
    );
    assertCondition(
      upgrade.levels[0] === 0,
      `${upgrade.id} levels must start at zero`,
    );
    for (const [index, level] of upgrade.levels.entries()) {
      assertCondition(
        level === index,
        `${upgrade.id} levels must be contiguous`,
      );
    }
    for (const cost of upgrade.costs) {
      validatePositiveInteger(cost, `${upgrade.id} cost`);
    }
    for (const effect of upgrade.effects) {
      validatePositiveNumber(effect, `${upgrade.id} effect`);
    }
    validateStrictlyIncreasing(upgrade.costs, `${upgrade.id} costs`);
    validateStrictlyIncreasing(upgrade.effects, `${upgrade.id} effects`);
    if (upgrade.id === "starting-size") {
      for (const effect of upgrade.effects) {
        validatePositiveInteger(effect, "starting-size effect");
        assertCondition(
          effect <= config.growth.sizes.length,
          "starting-size effects must stay inside the growth table",
        );
      }
    }
    if (upgrade.id === "movement-speed") {
      for (const effect of upgrade.effects) {
        assertCondition(
          effect <= config.movement.maximumSpeed,
          "movement-speed effects must not exceed maximum speed",
        );
      }
    }
  }
};

const validateContent = (config: GameplayConfig): void => {
  assertCondition(
    config.skins.length >= 6 && config.skins.length <= 10,
    "skins must contain between six and ten entries",
  );
  validateUniqueIds(config.skins, "skins");
  const defaultSkin = config.skins.find(
    (skin) => skin.id === config.defaultSkinId,
  );
  assertCondition(defaultSkin !== undefined, "default skin must exist");
  assertCondition(
    defaultSkin.initiallyUnlocked,
    "default skin must start unlocked",
  );
  for (const skin of config.skins) {
    assertCondition(
      skin.displayName.trim() !== "",
      `${skin.id} name must not be empty`,
    );
    validatePositiveInteger(skin.bodyColor, `${skin.id} body color`);
    validatePositiveInteger(skin.accentColor, `${skin.id} accent color`);
    assertCondition(
      skin.bodyColor <= 0xffffff && skin.accentColor <= 0xffffff,
      `${skin.id} colors must be RGB values`,
    );
    if (skin.initiallyUnlocked) {
      assertCondition(
        skin.unlockCost === null,
        `${skin.id} default unlock must be free`,
      );
    } else {
      assertCondition(
        skin.unlockCost !== null,
        `${skin.id} unlock cost must exist`,
      );
      validatePositiveInteger(skin.unlockCost, `${skin.id} unlock cost`);
    }
  }
  assertCondition(
    config.foods.length >= 3,
    "foods must contain at least three entries",
  );
  validateUniqueIds(config.foods, "foods");
  for (const food of config.foods) {
    validatePositiveNumber(food.growth, `${food.id} growth`);
    validatePositiveNumber(food.radius, `${food.id} radius`);
  }
  validateExactIds(config.bonuses, expectedBonusIds, "bonuses");
  for (const bonus of config.bonuses) {
    validatePositiveInteger(bonus.durationMs, `${bonus.id} duration`);
    validatePositiveNumber(bonus.radius, `${bonus.id} radius`);
  }
  validateUniqueIds(config.rewards.entries, "rewards");
  for (const reward of config.rewards.entries) {
    validatePositiveInteger(reward.coins, `${reward.id} coins`);
  }
  const allIds = [
    config.arena.id,
    ...config.modes.map((entry) => entry.id),
    ...config.upgrades.map((entry) => entry.id),
    ...config.skins.map((entry) => entry.id),
    ...config.foods.map((entry) => entry.id),
    ...config.bonuses.map((entry) => entry.id),
    ...config.rewards.entries.map((entry) => entry.id),
  ];
  for (const id of allIds) {
    validateIdentifier(id, "gameplay content identifier");
  }
  assertCondition(
    new Set(allIds).size === allIds.length,
    "gameplay content identifiers must be globally unique",
  );
};

const validatePlayerBody = (config: GameplayConfig): void => {
  const body = config.playerBody;
  validatePositiveInteger(body.baseSegmentCount, "base body segment count");
  validatePositiveInteger(body.levelBandSize, "body level band size");
  validatePositiveNumber(
    body.segmentSpacingScale,
    "body segment spacing scale",
  );
  validatePositiveNumber(body.bodyRadiusScale, "body radius scale");
  validatePositiveNumber(body.tailRadiusScale, "tail radius scale");
  assertCondition(
    body.tailRadiusScale < body.bodyRadiusScale,
    "tail radius scale must be below body radius scale",
  );
  validatePositiveNumber(body.minimumTailRadius, "minimum tail radius");
  validatePositiveNumber(
    body.interpolationDistance,
    "body interpolation distance",
  );
  validatePositiveNumber(
    body.historyMaximumDistance,
    "body history maximum distance",
  );
  const maximumSegmentCount = getSnakeBodySegmentCount(
    config.growth.maximumLevel,
    body,
  );
  const requiredHistoryDistance =
    maximumConfiguredValue(config.growth.sizes) *
    body.segmentSpacingScale *
    maximumSegmentCount;
  assertCondition(
    body.historyMaximumDistance >= requiredHistoryDistance,
    "body history must contain every maximum-level segment",
  );
  validatePositiveInteger(body.rippleDurationMs, "body ripple duration");
  validatePositiveNumber(body.rippleAmplitude, "body ripple amplitude");
  assertCondition(
    body.rippleAmplitude < 1,
    "body ripple amplitude must be below one",
  );
  validatePositiveNumber(body.rippleWaveLength, "body ripple wave length");
  assertCondition(
    body.rippleWaveLength <= 1,
    "body ripple wave length must not exceed one",
  );
};

const validateSnakeVisual = (config: GameplayConfig): void => {
  const visual = config.snakeVisual;
  assertCondition(
    visual.anatomyId === "snake-v4-clear-face",
    "snake anatomy identifier must be snake-v4-clear-face",
  );
  validatePositiveNumber(visual.bodyOverlapScale, "snake body overlap scale");
  assertCondition(
    visual.bodyOverlapScale > 1,
    "snake body overlap scale must exceed one",
  );
  validatePositiveNumber(visual.bodyWidthScale, "snake body width scale");
  assertCondition(
    visual.bodyWidthScale < 1,
    "snake body width scale must slim the rendered body",
  );
  validatePositiveNumber(visual.eyeForwardScale, "snake eye forward scale");
  validatePositiveNumber(
    visual.eyeHighlightForwardScale,
    "snake eye highlight forward scale",
  );
  validatePositiveNumber(
    visual.eyeHighlightRadiusScale,
    "snake eye highlight radius scale",
  );
  validatePositiveNumber(visual.eyeRadiusScale, "snake eye radius scale");
  validatePositiveNumber(visual.eyeSideScale, "snake eye side scale");
  validatePositiveNumber(visual.headLengthScale, "snake head length scale");
  validatePositiveNumber(visual.headWidthScale, "snake head width scale");
  validatePositiveInteger(visual.homeLevel, "home snake level");
  validatePositiveInteger(
    visual.maximumUnscaledParts,
    "maximum unscaled snake parts",
  );
  assertCondition(
    visual.maximumUnscaledParts >= 4,
    "maximum unscaled snake parts must preserve complete anatomy",
  );
  validatePositiveNumber(visual.mouthForwardScale, "snake mouth forward scale");
  validatePositiveNumber(visual.neckRadiusScale, "snake neck radius scale");
  validatePositiveInteger(visual.outlineColor, "snake outline color");
  validatePositiveNumber(visual.outlineWidthScale, "snake outline width scale");
  validatePositiveInteger(visual.patternStepPx, "snake pattern step");
  validatePositiveNumber(visual.tailBaseScale, "snake tail base scale");
  validatePositiveNumber(visual.tailPointScale, "snake tail point scale");
  validatePositiveNumber(
    visual.tailTipRadiusScale,
    "snake tail tip radius scale",
  );
  validatePositiveNumber(visual.tongueForkScale, "snake tongue fork scale");
  validatePositiveNumber(visual.tongueLengthScale, "snake tongue length scale");
  assertCondition(
    visual.eyeRadiusScale > visual.eyeHighlightRadiusScale,
    "snake eye highlight must fit inside the eye",
  );
  assertCondition(
    visual.tongueForkScale < visual.tongueLengthScale,
    "snake tongue fork must be shorter than its tongue",
  );
  assertCondition(
    visual.homeLevel <= config.growth.maximumLevel,
    "home snake level must stay inside the growth table",
  );
};

const validateSnakeMotion = (config: GameplayConfig): void => {
  const motion = config.snakeMotion;
  validatePositiveInteger(motion.blinkCycleMs, "snake blink cycle");
  validatePositiveInteger(motion.blinkDurationMs, "snake blink duration");
  validatePositiveInteger(motion.chompDurationMs, "snake chomp duration");
  validatePositiveNumber(motion.mouthOpenScale, "snake mouth open scale");
  validatePositiveNumber(
    motion.slitherAmplitudeScale,
    "snake slither amplitude",
  );
  validatePositiveInteger(motion.slitherCycleMs, "snake slither cycle");
  validatePositiveNumber(
    motion.slitherWavelengthSegments,
    "snake slither wavelength",
  );
  validatePositiveNumber(motion.tongueFlickScale, "snake tongue flick scale");
  assertCondition(
    motion.blinkDurationMs < motion.blinkCycleMs,
    "snake blink duration must be shorter than its cycle",
  );
  assertCondition(
    motion.mouthOpenScale < 1,
    "snake mouth open scale must be below one",
  );
  assertCondition(
    motion.slitherAmplitudeScale < 1,
    "snake slither amplitude must be below one",
  );
  assertCondition(
    motion.tongueFlickScale < 1,
    "snake tongue flick scale must be below one",
  );
};

const validateTuning = (config: GameplayConfig): void => {
  validatePositiveInteger(config.bonusPopulation.target, "bonus target");
  validatePositiveInteger(
    config.bonusPopulation.maximumSpawnAttemptsPerBonus,
    "bonus spawn attempts",
  );
  validatePositiveNumber(
    config.bonusPopulation.initialPlayerExclusionRadius,
    "bonus player exclusion radius",
  );
  validatePositiveNumber(
    config.bonusPopulation.minimumSpacing,
    "bonus minimum spacing",
  );
  validatePositiveNumber(
    config.bonusPopulation.spawnInset,
    "bonus spawn inset",
  );
  assertCondition(
    config.round.durationMs === 180_000,
    "standard round duration must be exactly 180000 milliseconds",
  );
  validatePositiveInteger(
    config.runtimeStability.maximumProcessedEvents,
    "maximum processed runtime events",
  );
  validatePositiveInteger(
    config.coinRewardFeedback.sparkCount,
    "coin feedback spark count",
  );
  validatePositiveNumber(
    config.coinRewardFeedback.sparkRadius,
    "coin feedback spark radius",
  );
  validatePositiveNumber(
    config.coinRewardFeedback.sparkStartDistance,
    "coin feedback spark start distance",
  );
  validatePositiveNumber(
    config.coinRewardFeedback.sparkTravelDistance,
    "coin feedback spark travel distance",
  );
  validatePositiveNumber(
    config.strengthReadability.opponentCueDistance,
    "opponent cue distance",
  );
  validatePositiveNumber(
    config.virtualJoystick.deadZoneRadius,
    "joystick dead zone radius",
  );
  validatePositiveNumber(
    config.virtualJoystick.knobRadius,
    "joystick knob radius",
  );
  validatePositiveNumber(
    config.virtualJoystick.ringRadius,
    "joystick ring radius",
  );
  validateNonNegativeInteger(
    config.virtualJoystick.safeAreaInset,
    "joystick safe area inset",
  );
  assertCondition(
    config.virtualJoystick.deadZoneRadius < config.virtualJoystick.ringRadius,
    "joystick dead zone must be inside the ring",
  );
  assertCondition(
    config.virtualJoystick.knobRadius < config.virtualJoystick.ringRadius,
    "joystick knob must be inside the ring",
  );
  validateAlpha(
    config.virtualJoystick.visual.connectorAlpha,
    "joystick connector alpha",
  );
  validateAlpha(config.virtualJoystick.visual.fillAlpha, "joystick fill alpha");
  validateAlpha(config.virtualJoystick.visual.knobAlpha, "joystick knob alpha");
  validateAlpha(
    config.virtualJoystick.visual.outlineAlpha,
    "joystick outline alpha",
  );
  validateAlpha(config.virtualJoystick.visual.ringAlpha, "joystick ring alpha");
  validatePositiveNumber(config.arena.width, "arena width");
  validatePositiveNumber(config.arena.height, "arena height");
  validatePositiveNumber(config.arena.boundaryInset, "arena boundary inset");
  validatePositiveNumber(
    config.arena.boundaryPushDistance,
    "arena boundary push distance",
  );
  validatePositiveNumber(
    config.arena.cameraLookAheadDistance,
    "camera look-ahead distance",
  );
  validatePositiveNumber(
    config.arena.cameraFollowRatePerSecond,
    "camera follow rate",
  );
  const maximumPlayerRadius = maximumConfiguredValue(config.growth.sizes);
  const requiredArenaInset =
    config.arena.boundaryInset +
    config.arena.boundaryPushDistance +
    maximumPlayerRadius;
  assertCondition(
    config.arena.width > requiredArenaInset * 2,
    "arena width must contain the largest player",
  );
  assertCondition(
    config.arena.height > requiredArenaInset * 2,
    "arena height must contain the largest player",
  );
  validatePositiveNumber(config.movement.baseSpeed, "base speed");
  validateFiniteNumber(
    config.movement.initialHeadingRadians,
    "initial heading",
  );
  validateFiniteNumber(config.movement.initialPosition.x, "initial position x");
  validateFiniteNumber(config.movement.initialPosition.y, "initial position y");
  const initialPlayerRadius = maximumConfiguredValue(config.growth.sizes);
  const initialPlayerInset = config.arena.boundaryInset + initialPlayerRadius;
  assertCondition(
    config.movement.initialPosition.x >= initialPlayerInset &&
      config.movement.initialPosition.x <=
        config.arena.width - initialPlayerInset,
    "initial position x must contain the largest player",
  );
  assertCondition(
    config.movement.initialPosition.y >= initialPlayerInset &&
      config.movement.initialPosition.y <=
        config.arena.height - initialPlayerInset,
    "initial position y must contain the largest player",
  );
  validatePositiveNumber(config.movement.maximumSpeed, "maximum speed");
  assertCondition(
    config.movement.maximumSpeed >= config.movement.baseSpeed,
    "maximum speed must not be below base speed",
  );
  validatePositiveNumber(config.movement.acceleration, "acceleration");
  validatePositiveNumber(config.movement.turnRateRadiansPerSecond, "turn rate");
  validatePositiveInteger(config.combat.defeatLevelGap, "defeat level gap");
  validateNonNegativeInteger(
    config.combat.contactDistancePadding,
    "combat contact distance padding",
  );
  validatePositiveInteger(
    config.combat.contactMaximumAgeMs,
    "combat contact maximum age",
  );
  assertCondition(
    config.combat.contactMaximumAgeMs >= config.simulation.fixedStepMs,
    "combat contact maximum age must include one fixed step",
  );
  validatePositiveNumber(
    config.combat.separationDistance,
    "separation distance",
  );
  validatePositiveInteger(
    config.combat.playerRespawnDelayMs,
    "player respawn delay",
  );
  validateNonNegativeInteger(
    config.combat.playerRespawnLevelLoss,
    "player respawn level loss",
  );
  validatePositiveInteger(
    config.combat.playerRespawnGridColumns,
    "player respawn grid columns",
  );
  validatePositiveInteger(
    config.combat.playerRespawnGridRows,
    "player respawn grid rows",
  );
  validatePositiveNumber(
    config.combat.playerRespawnSafeDistance,
    "player respawn safe distance",
  );
  validateNonNegativeInteger(
    config.combat.playerRespawnSpawnInset,
    "player respawn spawn inset",
  );
  validatePositiveNumber(
    config.tailClipping.bodySeparationDistance,
    "tail clipping body separation distance",
  );
  validatePositiveNumber(
    config.tailClipping.feedbackDurationMs,
    "tail clipping feedback duration",
  );
  validatePositiveNumber(
    config.tailClipping.mouthForwardScale,
    "tail clipping mouth forward scale",
  );
  validatePositiveNumber(
    config.tailClipping.mouthRadiusScale,
    "tail clipping mouth radius scale",
  );
  assertCondition(
    config.combat.playerRespawnLevelLoss < config.growth.sizes.length,
    "player respawn level loss must stay inside the growth table",
  );
  validatePositiveInteger(config.bots.targetActive, "bot target");
  validatePositiveInteger(config.bots.minimumActive, "minimum bots");
  validatePositiveInteger(config.bots.maximumActive, "maximum bots");
  assertCondition(
    config.bots.minimumActive === 12 &&
      config.bots.maximumActive === 20 &&
      config.bots.targetActive >= config.bots.minimumActive &&
      config.bots.targetActive <= config.bots.maximumActive,
    "bot target must be between 12 and 20",
  );
  validatePositiveNumber(config.bots.perceptionRadius, "bot perception radius");
  validatePositiveNumber(
    config.bots.initialPlayerExclusionRadius,
    "initial player bot exclusion radius",
  );
  validatePositiveInteger(
    config.bots.regenerationCadenceMs,
    "bot regeneration cadence",
  );
  validatePositiveInteger(
    config.bots.regenerationFallbackColumns,
    "bot regeneration fallback columns",
  );
  validatePositiveInteger(
    config.bots.regenerationFallbackRows,
    "bot regeneration fallback rows",
  );
  validateNonNegativeInteger(
    config.bots.regenerationPlayerClearance,
    "bot regeneration player clearance",
  );
  validatePositiveInteger(
    config.bots.maximumSpawnAttemptsPerBot,
    "maximum bot spawn attempts",
  );
  validateNonNegativeInteger(config.bots.minimumSpacing, "minimum bot spacing");
  validatePositiveNumber(config.bots.movementSpeed, "bot movement speed");
  validateNonNegativeInteger(config.bots.spawnInset, "bot spawn inset");
  validatePositiveNumber(
    config.bots.debugNearMinimumDistance,
    "debug near-bot minimum distance",
  );
  validatePositiveNumber(
    config.bots.debugNearMaximumDistance,
    "debug near-bot maximum distance",
  );
  assertCondition(
    config.bots.debugNearMinimumDistance < config.bots.debugNearMaximumDistance,
    "debug near-bot distances must be ordered",
  );
  validatePositiveInteger(config.bots.minimumLevel, "minimum bot level");
  validatePositiveInteger(config.bots.maximumLevel, "maximum bot level");
  validateNonNegativeInteger(
    config.bots.maximumLevelAbovePlayer,
    "maximum bot levels above player",
  );
  assertCondition(
    config.bots.minimumLevel <= config.bots.maximumLevel &&
      config.bots.maximumLevel === config.growth.maximumLevel,
    "bot levels must stay inside the growth table",
  );
  validateFiniteNumber(config.bots.aggressionMinimum, "minimum bot aggression");
  validateFiniteNumber(config.bots.aggressionMaximum, "maximum bot aggression");
  assertCondition(
    config.bots.aggressionMinimum >= 0 &&
      config.bots.aggressionMinimum < config.bots.aggressionMaximum &&
      config.bots.aggressionMaximum <= 1,
    "bot aggression range must stay from zero through one",
  );
  validatePositiveNumber(
    config.bots.perceptionRadiusMinimum,
    "minimum bot perception radius",
  );
  validatePositiveNumber(
    config.bots.perceptionRadiusMaximum,
    "maximum bot perception radius",
  );
  assertCondition(
    config.bots.perceptionRadiusMinimum < config.bots.perceptionRadiusMaximum,
    "bot perception radius range must be ordered",
  );
  validateFiniteNumber(
    config.bots.steeringNoiseMinimum,
    "minimum bot steering noise",
  );
  validatePositiveNumber(
    config.bots.steeringNoiseMaximum,
    "maximum bot steering noise",
  );
  assertCondition(
    config.bots.steeringNoiseMinimum < config.bots.steeringNoiseMaximum,
    "bot steering noise range must be ordered",
  );
  assertCondition(
    config.bots.targetPreferences.length === 3 &&
      new Set(config.bots.targetPreferences).size === 3 &&
      config.bots.targetPreferences.every((preference) =>
        ["food", "hunt", "wander"].includes(preference),
      ),
    "bot target preferences must contain the exact identifier set",
  );
  validatePositiveInteger(config.bots.huntLevelGap, "bot hunt level gap");
  validatePositiveInteger(config.bots.fleeLevelGap, "bot flee level gap");
  assertCondition(
    config.combat.defeatLevelGap === config.bots.huntLevelGap &&
      config.combat.defeatLevelGap === config.bots.fleeLevelGap,
    "combat and bot meaningful level gaps must match",
  );
  validateNonNegativeInteger(
    config.bots.targetLevelHysteresis,
    "bot target level hysteresis",
  );
  assertCondition(
    config.bots.targetLevelHysteresis <
      Math.min(config.bots.huntLevelGap, config.bots.fleeLevelGap),
    "bot target level hysteresis must preserve equal-strength behavior",
  );
  validatePositiveNumber(
    config.bots.targetRangeHysteresis,
    "bot target range hysteresis",
  );
  validatePositiveInteger(
    config.bots.retargetCooldownMs,
    "bot retarget cooldown",
  );
  validatePositiveInteger(
    config.bots.stuckDetectionMs,
    "bot stuck detection time",
  );
  validatePositiveInteger(
    config.bots.recoveryDurationMs,
    "bot recovery duration",
  );
  validatePositiveNumber(
    config.bots.stuckDistanceEpsilon,
    "bot stuck distance epsilon",
  );
  validatePositiveNumber(
    config.bots.targetSwitchMinimumAdvantage,
    "bot target switch minimum advantage",
  );
  validatePositiveNumber(config.bots.turnRateRadiansPerSecond, "bot turn rate");
  validatePositiveNumber(
    config.bots.wanderTargetReachDistance,
    "bot wander target reach distance",
  );
  validatePositiveInteger(config.foodPopulation.minimum, "minimum food");
  validatePositiveInteger(config.foodPopulation.target, "target food");
  validatePositiveInteger(config.foodPopulation.maximum, "maximum food");
  validatePositiveInteger(
    config.foodPopulation.maximumSpawnAttemptsPerFood,
    "maximum food spawn attempts",
  );
  validatePositiveNumber(
    config.foodPopulation.initialPlayerExclusionRadius,
    "initial player food exclusion radius",
  );
  validatePositiveNumber(
    config.foodPopulation.debugNearMinimumDistance,
    "debug near-food minimum distance",
  );
  validatePositiveNumber(
    config.foodPopulation.debugNearMaximumDistance,
    "debug near-food maximum distance",
  );
  validateNonNegativeInteger(
    config.foodPopulation.minimumFoodSpacing,
    "minimum food spacing",
  );
  validateNonNegativeInteger(
    config.foodPopulation.spawnInset,
    "food spawn inset",
  );
  assertCondition(
    config.foodPopulation.minimum <= config.foodPopulation.target,
    "minimum food must not exceed target food",
  );
  assertCondition(
    config.foodPopulation.target <= config.foodPopulation.maximum,
    "target food must not exceed maximum food",
  );
  assertCondition(
    config.foodPopulation.debugNearMinimumDistance <
      config.foodPopulation.debugNearMaximumDistance,
    "debug near-food distances must be ordered",
  );
  const maximumFoodRadius = Math.max(
    ...config.foods.map((food) => food.radius),
  );
  const foodArenaInset =
    config.arena.boundaryInset +
    config.foodPopulation.spawnInset +
    maximumFoodRadius;
  assertCondition(
    config.arena.width > foodArenaInset * 2,
    "arena width must contain food spawn bounds",
  );
  assertCondition(
    config.arena.height > foodArenaInset * 2,
    "arena height must contain food spawn bounds",
  );
  validatePositiveInteger(
    config.rewards.botFoodDropMinimum,
    "minimum bot food drop",
  );
  validatePositiveInteger(
    config.rewards.botFoodDropMaximum,
    "maximum bot food drop",
  );
  assertCondition(
    config.rewards.botFoodDropMinimum <= config.rewards.botFoodDropMaximum,
    "minimum bot food drop must not exceed maximum",
  );
  validatePositiveNumber(
    config.rewards.botFoodDropMinimumDistance,
    "minimum bot food drop distance",
  );
  validatePositiveNumber(
    config.rewards.botFoodDropMaximumDistance,
    "maximum bot food drop distance",
  );
  assertCondition(
    config.rewards.botFoodDropMinimumDistance <
      config.rewards.botFoodDropMaximumDistance,
    "bot food drop distances must be ordered",
  );
  assertCondition(
    typeof config.rewards.botDefeatPlayerRewardOnly === "boolean",
    "bot defeat reward policy must be boolean",
  );
  validatePositiveNumber(
    config.simulation.fixedStepMs,
    "simulation fixed step",
  );
  validatePositiveNumber(
    config.simulation.maximumRealFrameDeltaMs,
    "maximum real frame delta",
  );
  assertCondition(
    config.simulation.maximumRealFrameDeltaMs >= config.simulation.fixedStepMs,
    "maximum real frame delta must not be below the fixed step",
  );
};

export const validateGameplayConfig = (config: GameplayConfig): void => {
  validateModes(config);
  validateGrowth(config);
  validatePlayerBody(config);
  validateSnakeMotion(config);
  validateSnakeVisual(config);
  validateUpgrades(config);
  validateContent(config);
  validateTuning(config);
};
